import { Page, Progress } from "api/admin/referrals";
import { currentAdminCountryScope } from "helpers/storage";
import useAdminAccess from "hooks/useAdminAccess";
import { FormEvent, ReactNode, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { Button, errorMessage, fieldClass } from "../access-control/components";
export {
  Button,
  Card,
  ErrorState,
  LoadingRows,
  formatDate,
  formatLabel,
  errorMessage,
  fieldClass,
} from "../access-control/components";
export { Field } from "../gamification/components";
export const markets = ["NGN", "GHS", "KES"] as const;
export const queryOptions = { retry: false, refetchOnWindowFocus: false };
export const useReferralKey = () => {
  const { admin } = useAdminAccess();
  return ["adminReferrals", admin?.id, currentAdminCountryScope()];
};
export const money = (minor: number, currency: string) =>
  `${currency} ${new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(minor / 100)}`;
export function UserLink({
  id,
  children,
}: {
  id: number;
  children?: ReactNode;
}) {
  const { can } = useAdminAccess();
  const label = children || `User #${id}`;
  return can("referrals.records.view") ? (
    <Link className="underline text-orange-800" to={`/referrals/users/${id}`}>
      {label}
    </Link>
  ) : (
    <span>{label}</span>
  );
}
export function ProgressView({
  progress,
  currency,
}: {
  progress: Progress;
  currency: string;
}) {
  return (
    <ul className="space-y-1 text-sm">
      <li>
        Tier {progress.tier.reachedTier} / {progress.tier.requiredTier} ·{" "}
        {progress.tier.done ? "Completed" : "Incomplete"}
      </li>
      {progress.airtimeData && (
        <li>
          Airtime/data: {money(progress.airtimeData.totalMinor, currency)} /{" "}
          {money(progress.airtimeData.thresholdMinor, currency)}
        </li>
      )}
      <li>
        Crypto swaps: {money(progress.cryptoSwaps.totalUsdCents, "USD")} /{" "}
        {money(progress.cryptoSwaps.thresholdUsdCents, "USD")}
      </li>
    </ul>
  );
}
export function Pages({
  meta,
  busy,
  change,
}: {
  meta?: Page<unknown>["meta"];
  busy: boolean;
  change: (page: number) => void;
}) {
  if (!meta) return null;
  return (
    <nav
      aria-label="Pagination"
      className="flex items-center gap-3 mt-4 text-sm"
    >
      <Button
        variant="secondary"
        disabled={busy || !meta.hasPreviousPage}
        onClick={() => change(meta.currentPage - 1)}
      >
        Previous
      </Button>
      <span>
        Page {meta.currentPage} of {Math.max(1, meta.totalPages)}
      </span>
      <Button
        variant="secondary"
        disabled={busy || !meta.hasNextPage}
        onClick={() => change(meta.nextPage ?? meta.currentPage + 1)}
      >
        Next
      </Button>
    </nav>
  );
}
export function ActionForm({
  title,
  description,
  run,
  done,
  cancel,
  allowed,
}: {
  title: string;
  description: string;
  run: (reason: string) => Promise<string>;
  done: (message: string) => void;
  cancel: () => void;
  allowed: boolean;
}) {
  const [reason, setReason] = useState("");
  const submitting = useRef(false);
  const cache = useQueryClient();
  const mutation = useMutation(run, { retry: false });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting.current || !allowed || !reason.trim()) return;
    submitting.current = true;
    try {
      const message = await mutation.mutateAsync(reason.trim());
      await cache.invalidateQueries("adminReferrals");
      done(message);
    } catch {
      // Keep the reason for retry; refresh stale records after a failed decision.
      void cache.invalidateQueries("adminReferrals");
    } finally {
      submitting.current = false;
    }
  };
  return (
    <form
      aria-label={title}
      onSubmit={submit}
      className="my-5 rounded-lg border border-orange-200 bg-orange-50 p-5"
    >
      <h2 className="font-semibold">{title}</h2>
      <p className="my-3 text-sm">{description}</p>
      <fieldset disabled={mutation.isLoading || !allowed}>
        <label className="block text-sm">
          Reason (required)
          <textarea
            autoFocus
            required
            className={fieldClass}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <div className="flex gap-3 mt-4">
          <Button disabled={!reason.trim()} type="submit">
            {mutation.isLoading ? "Saving…" : `Confirm ${title.toLowerCase()}`}
          </Button>
          <Button type="button" variant="secondary" onClick={cancel}>
            Cancel
          </Button>
        </div>
      </fieldset>
      {mutation.isError && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {errorMessage(mutation.error)}
        </p>
      )}
    </form>
  );
}
export function downloadCsv(content: string, filename: string) {
  const url = URL.createObjectURL(
    new Blob([content], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
// Date fields are UTC boundaries. The API uses inclusive start / exclusive end.
export function dateWindow(from: string, to: string) {
  const start = new Date(`${from}T00:00:00.000Z`),
    end = new Date(`${to}T00:00:00.000Z`);
  const days = (end.getTime() - start.getTime()) / 86400000;
  if (!Number.isFinite(days) || days <= 0 || days > 366)
    throw new Error(
      "Choose an end date after the start date, within 366 days.",
    );
  return { from: start.toISOString(), to: end.toISOString() };
}
